// src/components/layout/Footer.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../hooks/useTheme';
import { Youtube, Linkedin, Facebook, Mail,Twitter } from 'lucide-react';

const Footer = () => {
  const { isDarkMode } = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const companyLinks = [
    { name: "Daftra.com", href: "https://www.daftra.com/en" },
    { name: "Knowledge base", href: "https://docs.daftra.com" },
    { name: "Daftra Developers", href: "https://daftara.dev/developer" },
  ];

  const socialLinks = [
    { name: "YouTube", icon: <Youtube size={18} />, href: "https://www.youtube.com/channel/UCUL9HYY4KkISNN1oHDWPRmA" },
    { name: "LinkedIn", icon: <Linkedin size={18} />, href: "https://www.linkedin.com/company/daftra/" },
    { name: "Facebook", icon: <Facebook size={18} />, href: "https://www.facebook.com/search/top?q=daftra%20-%20%D8%AF%D9%81%D8%AA%D8%B1%D8%A9" },
    // { name: "GitHub", icon: <Github size={18} />, href: "#" },
    // { name: "X", icon: <Twitter size={18} />, href: "https://x.com/daftraerp?lang=en" },
  ];

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
      style={{
        backgroundColor: isDarkMode ? '#111827' : '#1f2937',
        color: '#f9fafb',
        paddingTop: '4rem',
        paddingBottom: '2rem'
      }}
    >
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 2rem',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {/* Company Info */}
          <motion.div variants={itemVariants}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <span style={{ color: '#3b82f6', fontSize: '1.5rem', fontWeight: 'bold' }}>Daftra</span>
              <span style={{ color: '#f9fafb', fontSize: '1.5rem', fontWeight: 600 }}>API</span>
            </div>
            <p style={{
              color: 'rgba(249, 250, 251, 0.8)',
              marginBottom: '1.5rem',
              maxWidth: '20rem',
              lineHeight: 1.6
            }}>
              Powerful API solutions for ERP and accounting systems.
              Connect, integrate, and extend your financial workflows.
            </p>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '1.5rem'
            }}>
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  aria-label={link.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#f9fafb',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.closest('a').style.backgroundColor = '#3b82f6';
                      e.target.closest('a').style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.closest('a').style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.closest('a').style.transform = 'translateY(0)';
                    }}
                  >
                    {link.icon}
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Company Links */}
          <motion.div variants={itemVariants}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '1.25rem',
              color: '#f9fafb'
            }}>Company</h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {companyLinks.map((link, index) => (
                <li key={index} style={{ marginBottom: '0.75rem' }}>
                  <a
                    href={link.href}
                    style={{
                      color: 'rgba(249, 250, 251, 0.8)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseOver={(e) => e.target.style.color = '#3b82f6'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(249, 250, 251, 0.8)'}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact - Email Only */}
          <motion.div variants={itemVariants}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '1.25rem',
              color: '#f9fafb'
            }}>Contact Us</h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: 'rgba(249, 250, 251, 0.8)'
              }}>
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  padding: '0.5rem',
                  display: 'flex',
                }}>
                  <Mail size={16} />
                </div>
                <span>support@daftra.com</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={itemVariants}
          style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            paddingTop: '2rem',
            marginTop: '2rem',
            textAlign: 'center',
            color: 'rgba(249, 250, 251, 0.6)',
            fontSize: '0.875rem'
          }}
        >
          <p>© {new Date().getFullYear()} Daftra. All rights reserved.</p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;